export class ArrayUtils {
  /**
   * @return true if the array a is missing an element with the same id that is present in array b
   */
  static aMissingElementFromBByKey<T, I>(
    a: T[],
    b: T[],
    keyAccessor: (obj: T) => I = (obj: T) => (obj as unknown as any)?.id,
  ): boolean {
    let missingElement = false;

    for (const bElement of b) {
      if (!a.find(aElement => keyAccessor(aElement) === keyAccessor(bElement))) {
        missingElement = true;
        break;
      }
    }

    return missingElement;
  }

  static aMissingElementFromB<T>(a: T[], b: T[]): boolean {
    let missingElement = false;

    for (const bElement of b) {
      if (!a.includes(bElement)) {
        missingElement = true;
        break;
      }
    }

    return missingElement;
  }

  /**
   * Returns true if the arrays both contain the same elements.
   * Note: null arrays and empty arrays are considered different
   *
   * Duplicate elements are allowed is either array.
   */
  static arraysAreEqual<T>(a: T[], b: T[]): boolean {
    if (a == null && b != null) return false;
    if (a != null && b == null) return false;
    if (a == null && b == null) return true;

    return !(ArrayUtils.aMissingElementFromB(a, b) || ArrayUtils.aMissingElementFromB(b, a));
  }

  static compareArraysByMemberId<T>(a: T[], b: T[]): boolean {
    if (a == null && b != null) return false;
    if (a != null && b == null) return false;
    if (a == null && b == null) return true;

    return !(
      ArrayUtils.aMissingElementFromBByKey(a, b) || ArrayUtils.aMissingElementFromBByKey(b, a)
    );
  }

  /**
   * Sort an array of objects alphabetically based on the given property name
   * @param toSort The array to sort
   * @param prop The property on which to sort the array alphabetically
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static sortArrayOfObjectsAlphabeticallyByProperty(toSort: any[], prop: string): void {
    toSort.sort((a, b) => {
      if (a[prop] < b[prop]) {
        return -1;
      }
      if (a[prop] > b[prop]) {
        return 1;
      }
      return 0;
    });
  }

  /**
   * Reverse a 2D array like [[1, 2, 3], [4, 5, 6]] => [[4, 5, 6], [1, 2, 3]]
   * @param a 2D Array
   * @returns the revesed 2D array
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static reverseLUT2DArrayOrder(a: any[][]): any[][] {
    const b = [...a];
    const tmp = b[0];
    b[0] = b[1];
    b[1] = tmp;
    return b;
  }

  /**
   * Return true if the 2D arrays contain equal values, false otherwise
   * @param a a 2D array
   * @param b a 2D array
   * @returns a boolean: true if arrays contain the same items, false otherwise
   */
  static checkEqualityOfNumeric2DArrays(
    a: number[][] | string[][],
    b: number[][] | string[][],
  ): boolean {
    if (a && b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        for (let j = 0; j < Math.min(a[i].length, b[i].length); j++) {
          if (a[i][j] !== b[i][j]) {
            return false;
          }
        }
      }
      return true;
    }
    return false;
  }

  /**
   * Add a ModelObject to an array if it is not already there
   * @param array an array of objects with id property
   * @param obj an object with id property
   */
  static addToArrayIfNotAlreadyThere(array: { id: number }[], obj: { id: number }) {
    const index = this.getIndexOfObjectWithIdInArray(array, obj);
    if (index === -1) {
      array.push(obj);
    }
  }

  /**
   * Get the index of a ModelObject within an array if it exists. Return -1 otherwise
   * @param array An array in which to search for obj
   * @param obj An object with id property for which to search
   * @returns The index of the object or -1 if it is not present in the given array
   */
  static getIndexOfObjectWithIdInArray(array: any[], obj: any): number {
    return array.findIndex(d => d.id === obj.id);
  }

  /**
   * Move an element in array by inserting it at the given index and shifting other elements if needed
   * @param array An array in which to move an element
   * @param oldIndex A number
   * @param newIndex A number
   * @returns The array with the element moved
   */
  static moveItemInArray(array: any[], oldIndex: number, newIndex: number): any[] {
    // algorithm from: https://stackoverflow.com/questions/5306680/move-an-array-element-from-one-array-position-to-another
    // if (newIndex >= oldIndex) {
    //   let k = newIndex - array.length + 1;
    //   while (k--) {
    //     console.log('k: ', k, array);
    //     array.push(null);
    //   }
    // }
    // array.splice(newIndex, 0, array.splice(oldIndex, 1)[0]);
    // return array;
    const element = { ...array[oldIndex] };
    array.splice(oldIndex, 1);
    array.splice(newIndex, 0, element);
    return array;
  }
}
